
import { namespace } from 'vuex-class';
import { Vue, Component, Prop, Watch, PropSync } from 'vue-property-decorator';
import * as tus from 'tus-js-client';
// interfaces
import { ParamDictionary } from '@/interfaces/ParamDictionary';
import { ExerciseVM } from '@/interfaces/ExerciseVM';
import rest from '@/rest';
import { ExBundleVM } from '@/interfaces/ExBundleAssignmentGroupVM';

const globals = namespace("globals");

@Component({})
export default class TusFileUploadOverlay extends Vue {
  // @Prop({ default: null }) file!: File | null;
  @PropSync("file") syncedFile!: File | null;
  @Prop() relativePath!: string; // optional relative path to upload file to (e.g. "images/" or "documents/")
  @Prop({ default: true }) autoClose!: boolean;

  // @globals.Getter selectedExercise!: ExerciseVM;
  @globals.Getter selectedBundle!: ExBundleVM;

  uploadInstance: any = null;
  uploadProgress: number = 0;
  uploading: boolean = false;
  fileName: string = '';

  get visible() {
    return !!this.syncedFile;
  }

  get progressLabel() {
    return this.uploading ? `${Math.round(this.uploadProgress)} %` : (this.uploadProgress > 0 ? 'Fertig' : 'Bereit');
  }

  get themeColor() {
    try {
      if (this.$store && this.$store.state && this.$store.state.ux && this.$store.state.ux.dcBlue)
        return this.$store.state.ux.dcBlue;
    } catch (e) {
      // ignore
    }
    return 'primary';
  }

  mounted() {
    if (this.syncedFile) {
      this.startUpload(this.syncedFile);
    }
  }

  @Watch('syncedFile')
  onFileChanged(newFile: File | null) {
    if (newFile) {
      this.startUpload(newFile);
    } else {
      // reset state
      this.uploadProgress = 0;
      this.uploading = false;
      this.fileName = '';
    }
  }

  startUpload(file: File) {
    if (!file) return;
    this.fileName = file.name;
    this.uploading = true;
    this.uploadProgress = 0;

    const token = window.localStorage.getItem('digiClassAuth');

    const upload = new tus.Upload(file, {
      endpoint: "api/files/",
      retryDelays: [0, 3000, 5000, 10000],
      chunkSize: 10000000,
      metadata: {
        filename: file.name,
        filetype: file.type
      },
      headers: token ? { Authorization: 'Bearer ' + token } : {},
      onError: (err: any) => {
        this.uploading = false;
        // this.$emit('uploaded:TusFileUploadOverlay', { success: false, fileName: file.name, message: String(err) });
        this.$store.commit("ux/SB_FAILURE", {
          message: String(err),
          timeout: 0
        });
      },
      onProgress: (bytesUploaded: number, bytesTotal: number) => {
        this.uploadProgress = (bytesUploaded * 100) / bytesTotal;
      },
      onSuccess: async () => {
        const url = (upload.url) ? upload.url : null;
        this.uploading = false;
        this.uploadProgress = 100;
        await this.moveFileToBundleDir(file.name);
        if (this.autoClose) {
          // slight delay so user sees 100%
          await this.$globalHelper.delay(400);
          // console.log("closeOverlay");
          this.closeOverlay();
        }
      }
    });

    this.uploadInstance = upload;

    upload.findPreviousUploads().then((previousUploads: any[]) => {
      if (previousUploads.length) {
        upload.resumeFromPreviousUpload(previousUploads[0]);
      }
      upload.start();
    }).catch(() => {
      upload.start();
    });
  }

  async moveFileToBundleDir(fileName: string): Promise<void> {
    let params: ParamDictionary = { dictionary: {}};
    params.dictionary!["OldFilePath"] = "";
    params.dictionary!["UploadedFileName"] = fileName;
    params.dictionary!["RelativePath"] = this.relativePath || "";
    params.dictionary!["BundleId"] = this.selectedBundle.id!.toString();
    await rest.url("contentManager/moveFileToBundleDir").post(params)
      .then(async (response) => {
        // console.log("moveFileToBundleDir response:", response);
        if (!response) {
          this.$store.commit("ux/SB_FAILURE", {
            message: "Fehler beim Verschieben der Datei in das Schulungsverzeichnis.",
            timeout: 0
          });

          return;
        }

        this.$root.$emit('fileListUpdate:MediaPool'); // notify media pool via global event to refresh file list
        // console.log("emitting uploaded:TusFileUploadOverlay");
        this.$emit('uploaded:TusFileUploadOverlay', response);
      })
      .catch(err => {
      });
  }

  cancelUpload() {
    if (this.uploadInstance) {
      try {
        this.uploadInstance.abort();
      } catch (e) {
        // ignore
      }
      this.uploading = false;
      this.$emit('cancelled:TusFileUploadOverlay', { fileName: this.fileName });
      this.closeOverlay();
    }
  }

  closeOverlay() {
    this.syncedFile = null;
  }
}
